/**
 * Pure helpers for BioSDK REST path selection (`/mosip`, `/iso`, `/raw` namespaces).
 * Kept separate from `config.js` for unit testing without `window` / env.
 */

export const trimTrailingSlash = (u) => (u || '').replace(/\/$/, '');

export const buildBiosdkRawBaseUrl = (biosdkBaseUrl) => `${trimTrailingSlash(biosdkBaseUrl)}/raw`;
export const buildBiosdkMosipBaseUrl = (biosdkBaseUrl) => `${trimTrailingSlash(biosdkBaseUrl)}/mosip`;
export const buildBiosdkIsoBaseUrl = (biosdkBaseUrl) => `${trimTrailingSlash(biosdkBaseUrl)}/iso`;

export const ISO_NAMESPACE_MOSIP = 'mosip';
export const ISO_NAMESPACE_ISO = 'iso';
export const ISO_NAMESPACE_STORAGE_KEY = 'biosdk.isoNamespace';

export const normalizeIsoNamespace = (value) =>
  value === ISO_NAMESPACE_ISO ? ISO_NAMESPACE_ISO : ISO_NAMESPACE_MOSIP;

export const getStoredIsoNamespace = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return ISO_NAMESPACE_MOSIP;
  }
  return normalizeIsoNamespace(window.localStorage.getItem(ISO_NAMESPACE_STORAGE_KEY));
};

export const setStoredIsoNamespace = (namespace) => {
  const normalized = normalizeIsoNamespace(namespace);
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem(ISO_NAMESPACE_STORAGE_KEY, normalized);
  }
  return normalized;
};

/**
 * @param {string} biosdkBaseUrl - e.g. https://host/biosdk-service
 * @param {string} operationPath - e.g. extract-template, process, match
 * @param {boolean} isRaw - when true, use .../biosdk-service/raw/<operationPath>
 * @param {'mosip'|'iso'} isoNamespace - namespace for non-raw (default: mosip)
 */
export const buildBiosdkOperationUrl = (biosdkBaseUrl, operationPath, isRaw = false, isoNamespace = ISO_NAMESPACE_MOSIP) => {
  const normalizedIsoNamespace = normalizeIsoNamespace(isoNamespace);
  const base = isRaw
    ? buildBiosdkRawBaseUrl(biosdkBaseUrl)
    : (normalizedIsoNamespace === ISO_NAMESPACE_ISO
      ? buildBiosdkIsoBaseUrl(biosdkBaseUrl)
      : buildBiosdkMosipBaseUrl(biosdkBaseUrl));
  const path = String(operationPath || '').replace(/^\//, '');
  return `${base}/${path}`;
};

/**
 * Client-only observability: ISO vs raw route selection (not sent on the wire).
 * @param {string} componentName
 * @param {'isoRoute'|'rawRoute'} routeTag
 * @param {string} url
 * @param {Record<string, unknown>} [meta]
 */
export const logBiosdkUiRoute = (componentName, routeTag, url, meta = {}) => {
  if (typeof console !== 'undefined' && typeof console.debug === 'function') {
    console.debug('[biosdk-ui]', { component: componentName, route: routeTag, url, ...meta });
  }
};
